/**
 * Shared HITL 3.0 padding token, defined once so the whole hitl_3_0 UI uses one
 * consistent inset and it can be tuned in a single place — the spacing sibling
 * of hitl3Typography's HITL3_FONT_SIZE.
 *
 * It is a CSS length STRING ('8px') on purpose, so it drops into BOTH makeStyles
 * rules (`padding: HITL3_PADDING`) and MUI `sx` (`padding` / `pl` / `px`:
 * HITL3_PADDING) without the number-vs-spacing-multiple ambiguity (in `sx` a
 * number like 8 would mean theme.spacing(8) = 64px, not 8px).
 *
 * IMPORTANT: apply this ONLY behind the hitl_3_0 flag / inside hitl3-only
 * components. The legacy layout keeps its own paddings and must not reference it.
 */
export const HITL3_PADDING = '8px';

/**
 * Gap between a chip's icon and its text in the hitl_7_0 Pulse strip.
 *
 * HITL-1663: the 3.0 chips leave 4–5px there, which is tuned for 14px text. At
 * the 12px chip size that gap reads as a hole between the icon and the word it
 * belongs to, and it costs width in a row that has to wrap as little as
 * possible. One token so all four chip kinds — express, reuse, reminder and the
 * response dial — space their icon identically.
 *
 * IMPORTANT: apply ONLY behind hitl_7_0.
 */
export const HITL7_CHIP_GAP = '3px';

/**
 * Shared TOTAL height of the three bottom sections of the hitl_7_0 console —
 * the Copilot strip (left), the typing box (centre) and the Cancel / Finish
 * action row (right) — in px, INCLUDING each section's own 1px top rule.
 *
 * HITL-1665: all three sit at the foot of panes that end on the same line, so
 * one shared height is the same thing as three aligned top rules. 47 is what the
 * action row measures: a 36px button in its 4px inset, plus the rule.
 *
 * TOTAL, not a minimum. A minimum is useless here — the typing box's natural
 * content (1px rule + 8px inset + 34px input + 8px inset) is 51px, so a 46px
 * floor left it 4px taller than the row it was supposed to match and nothing
 * looked different. Sections have to be told what to BE, and the typing box
 * trims its vertical inset to 6px to get there.
 *
 * It is a NUMBER, not a CSS string, because one of the three consumers is a
 * resizable panel sized in percent — react-resizable-panels v1 has no pixel
 * sizes — so the value has to be divided by a measured stack height before it
 * can be used there. See useAlignedStripPct.
 *
 * IMPORTANT: apply ONLY behind hitl_7_0.
 */
export const HITL7_BOTTOM_SECTION_HEIGHT = 47;

/**
 * The typing box's vertical inset, trimmed from HITL3_PADDING's 8px so the
 * section totals HITL7_BOTTOM_SECTION_HEIGHT: 1 + 6 + 34 + 6 = 47. The
 * horizontal inset stays at 8px, so the input still lines up with everything
 * else in the pane.
 */
export const HITL7_OMNIBAR_INSET_Y = '6px';

/**
 * Height of every control in the hitl_3_0 bottom bar, in px — the intent pills,
 * the Staff escalation pill, the Issue button and the language switch.
 *
 * HITL-1665: all four already agreed on 34 EXCEPT the language switch, which
 * carried 40 ("a bit larger than the intent pills so it's easier to hit"). In a
 * row that reads as one line, that 6px made the switch sit proud of everything
 * beside it and the whole bar look misaligned — a hit-area win that cost more
 * than it bought. One token so the next person cannot reintroduce the drift by
 * tuning one control in isolation.
 *
 * Not hitl_7_0-gated: 34 is what the 3.0 bar has always used for its pills, so
 * this names an existing value rather than introducing a 7.0-only one. Only the
 * language switch's use of it is new, and that is gated by the caller.
 */
export const HITL3_BAR_CONTROL_HEIGHT = 34;

/**
 * HITL-1702 — the 7.0 header's gap between top-level items and between the
 * indicator cluster's groups. 6px at 1024 wide, rising to 8px from 1280 up.
 *
 * HITL-1662 squeezed these to 0–1px so the row held one line at 1024 with a
 * 46-character restaurant code. That tuning applied at EVERY width, so a wide
 * screen showed ~1000px of empty bar beside a crammed cluster. HITL-1702 folds
 * four indicators into one System icon and moves keyboard help into the profile
 * menu, which returns the width this gap spends at 1024; above that the gap
 * grows with the viewport, with no breakpoint or resize listener.
 *
 *   viewport   0.78125vw − 2px   clamped
 *   1024       6px               6px
 *   1280       8px               8px
 *   1920       13px              8px
 */
export const HITL7_HEADER_GAP = 'clamp(6px, calc(0.78125vw - 2px), 8px)';

/**
 * HITL-1702 — every 7.0 header icon is one fixed size: an 18px glyph in a 24px
 * box (3px inset). Legacy grows an ACTIVE icon's box (24 → 32 under HITL-1662's
 * 7.0 values, 32 → 42 before), so the cluster changed width as states changed
 * and a 2-row block could not hold its shape. Colour already carries the state.
 */
export const HITL7_HEADER_ICON_BOX = 24;
export const HITL7_HEADER_ICON_INSET = 3;
